import { createContext, ReactNode, useEffect } from "react";
import { mapAndFilterNulls } from "src/contexts/_shared/utils";
import { fileToDocumentWithContent } from "src/contexts/_shared/infrastructure/files/FileToCreateAnalysisRequest";
import { DocumentWithContentAcceptedTypes } from "src/contexts/_shared/domain/valueObjects/DocumentWithContentAcceptedContentType";
import { useNotifications } from "src/apps/hooks/useNotifications";
import { Notification } from "src/contexts/_shared/domain/Notification";
import { v4 as uuid } from "uuid";
import { NotificationStyles } from "src/contexts/_shared/domain/NotificationStyles";
import { StorageFileWithContent } from "src/contexts/_shared/domain/StorageFileWithContent";
import { IdentityVerificationDocumentSides } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentSides";
import {
  ERROR_GENERIC,
  ERROR_GENERIC_DESCRIPTION,
  WRONG_FILE,
  WRONG_FILE_MESSAGE,
  FILES_TOO_LARGE_TITLE,
  FILES_TOO_LARGE_DESCRIPTION,
  MAX_TOTAL_SIZE,
} from "./constants";
import { useUploadIdentityVerificationDocuments } from "src/apps/hooks/identityVerification/useUploadIdentityVerificationDocuments";
import { compressImage } from "./resizeFiles";
import {
  useSendAnalyticEvent,
  AnalyticsEvents,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";
import { useUploadActorOrderFiles } from "src/apps/hooks/uploadDocuments/useUploadActorOrderFiles";

const MAX_NON_COMPRESSABLE_IMAGE_SIZE = 3 * 1024 * 1024; // 3 MB

const hasAcceptedContentType = (file: File): boolean =>
  Object.values(DocumentWithContentAcceptedTypes).includes(
    file.type as DocumentWithContentAcceptedTypes,
  );

/**
 * Helper function to generate unique filename for generic
 * "image.jpg" files in iOS devices when taking pictures directly
 */
const generateUniqueFilename = (originalFilename: string): string => {
  if (originalFilename.toLowerCase() !== "image.jpg") {
    return originalFilename;
  }

  const randomNumber = Math.floor(Math.random() * 90000) + 10000; // 5-digit random number (10000-99999)
  const lastDotIndex = originalFilename.lastIndexOf(".");
  const nameWithoutExt = originalFilename.substring(0, lastDotIndex);
  const extension = originalFilename.substring(lastDotIndex);

  return `${nameWithoutExt}_${randomNumber}${extension}`;
};

export type UploaderContextType = {
  convertAndUploadActorOrderFiles: (orderId: string, files: File[]) => Promise<void>;
  convertAndUploadIdentityVerificationFiles: (
    orderId: string,
    side: IdentityVerificationDocumentSides,
    files: File[],
  ) => Promise<void>;
};

export const UploaderContext = createContext<UploaderContextType>({} as UploaderContextType);

export const UploaderProvider = ({ children }: { children: ReactNode }) => {
  const { addNotifications } = useNotifications();
  const { sendEvent } = useSendAnalyticEvent();
  const { uploadActorOrderFiles, isUploadActorOrderFilesError } = useUploadActorOrderFiles();

  const { mutate: uploadIdentityVerificationDocument, isError: isUploadIdentityVerificationError } =
    useUploadIdentityVerificationDocuments();

  useEffect(() => {
    if (isUploadActorOrderFilesError || isUploadIdentityVerificationError) {
      addNotifications([
        {
          id: uuid(),
          title: ERROR_GENERIC,
          message: ERROR_GENERIC_DESCRIPTION,
          style: NotificationStyles.ALERT,
          createdAt: new Date(),
        } as Notification,
      ]);
    }
  }, [isUploadActorOrderFilesError, isUploadIdentityVerificationError]);

  const uploadIsTooLarge = (analysisRequestDocuments: StorageFileWithContent[]) => {
    const totalSize = analysisRequestDocuments.reduce(
      (acc, doc) => acc + JSON.stringify(doc.toPrimitives()).length,
      0,
    );
    return totalSize >= MAX_TOTAL_SIZE;
  };

  const validateFileContentTypesAndNotify = (files: File[]): boolean => {
    if (!files.every(hasAcceptedContentType)) {
      addNotifications([
        {
          id: uuid(),
          title: WRONG_FILE,
          message: WRONG_FILE_MESSAGE,
          style: NotificationStyles.ALERT,
          createdAt: new Date(),
        } as Notification,
      ]);

      return false;
    }

    return true;
  };

  const trackUploadAnalytics = async (
    originalFiles: File[],
    processedFiles: File[],
    didExceedLimit: boolean,
  ) => {
    try {
      const fileCount = originalFiles.length;

      const originalTotalSizeBytes = originalFiles.reduce((acc, file) => acc + file.size, 0);
      const originalTotalSizeMB = +(originalTotalSizeBytes / (1024 * 1024)).toFixed(2);

      const processedTotalSizeBytes = processedFiles.reduce((acc, file) => acc + file.size, 0);
      const processedTotalSizeMB = +(processedTotalSizeBytes / (1024 * 1024)).toFixed(2);

      sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_FILES_UPLOADED, {
        file_count: fileCount,
        original_size_mb: originalTotalSizeMB,
        compressed_size_mb: processedTotalSizeMB,
        exceeded_limit: didExceedLimit,
        max_limit_mb_file_converted_to_base64: +(MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(2),
      });
    } catch (error) {
      console.error("Failed to track upload analytics:", error);
    }
  };

  const convertAndCompressFilesToDocuments = async (files: File[]) => {
    if (files.length == 0)
      return { documents: [], originalFiles: [], processedFiles: [], exceedsLimit: false };

    const originalFiles = [...files];
    if (!validateFileContentTypesAndNotify(files)) {
      return { documents: [], originalFiles, processedFiles: [], exceedsLimit: false };
    }

    const promises = files.map(async (file) => {
      if (file.type.startsWith("image/")) {
        const uniqueFilename = generateUniqueFilename(file.name);
        if (file.size > MAX_NON_COMPRESSABLE_IMAGE_SIZE) {
          const resizedBlob = await compressImage(file);
          if (resizedBlob) {
            file = new File([resizedBlob], uniqueFilename, { type: file.type });
          }
        }
        file = new File([file], uniqueFilename, { type: file.type });
      }

      const document = await fileToDocumentWithContent(file);
      return { file, document };
    });

    const settledPromises = await Promise.allSettled(promises);

    const results = mapAndFilterNulls(settledPromises, (promise) => {
      if (promise.status === "rejected") {
        const id = uuid();
        addNotifications([
          {
            id,
            title: WRONG_FILE,
            message: WRONG_FILE_MESSAGE,
            style: NotificationStyles.ALERT,
            createdAt: new Date(),
          } as Notification,
        ]);

        return null;
      }

      return promise.value;
    });

    const analysisRequestDocuments = results.map((result) => result.document);
    const processedFiles = results.map((result) => result.file);

    const exceedsLimit = uploadIsTooLarge(analysisRequestDocuments);
    if (exceedsLimit) {
      addNotifications([
        {
          id: uuid(),
          title: FILES_TOO_LARGE_TITLE,
          message: FILES_TOO_LARGE_DESCRIPTION,
          style: NotificationStyles.ALERT,
          createdAt: new Date(),
        } as Notification,
      ]);

      trackUploadAnalytics(originalFiles, processedFiles, true);

      return { documents: [], originalFiles, processedFiles: [], exceedsLimit };
    }

    return {
      documents: analysisRequestDocuments,
      originalFiles,
      processedFiles,
      exceedsLimit,
    };
  };

  const convertAndUploadActorOrderFiles = async (orderId: string, data: File[]) => {
    const result = await convertAndCompressFilesToDocuments(data);
    if (!result || result.documents.length === 0) {
      return;
    }

    const { documents, originalFiles, processedFiles, exceedsLimit } = result;

    trackUploadAnalytics(originalFiles, processedFiles, exceedsLimit);

    uploadActorOrderFiles({
      orderId,
      files: documents,
    });
  };

  const convertAndUploadIdentityVerificationFiles = async (
    orderId: string,
    side: IdentityVerificationDocumentSides,
    data: any,
  ) => {
    const result = await convertAndCompressFilesToDocuments(data);
    if (!result || result.documents.length === 0) {
      return;
    }

    const { documents, originalFiles, processedFiles, exceedsLimit } = result;

    trackUploadAnalytics(originalFiles, processedFiles, exceedsLimit);

    uploadIdentityVerificationDocument({
      orderId,
      side,
      file: documents[0],
    });
  };

  return (
    <UploaderContext.Provider
      value={{
        convertAndUploadActorOrderFiles,
        convertAndUploadIdentityVerificationFiles,
      }}
    >
      {children}
    </UploaderContext.Provider>
  );
};
